import * as THREE from 'three';
import FactoryFloorHeatMap from './FactoryFloorHeatMap';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

import { fetchMachineLocations } from '../services/PiLocationService';
import { loadObj } from './ObjLoader';
import { fetchNormalizedByInserts, fetchNormalizedByOrders, fetchTotalPowerConsumption } from '../services/FactoryViewService';
import { Attribute } from '../Types/Attribute';
import FactoryMachineManager from './FactoryMachineManager';
import FactoryRoomManager from './FactoryRoomManager';
import BackgroundMap from './BackgroundMap';
import { ColorsArray } from './Gradients';
import { clamp } from 'three/src/math/MathUtils';

import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';

class FactoryScene {
    private scene: THREE.Scene;
    private camera: THREE.OrthographicCamera;
    private renderer: THREE.WebGLRenderer;
    private controls: OrbitControls;
    private factoryFloor: FactoryFloorHeatMap;
    private factoryMachineManager: FactoryMachineManager;
    private factoryRoomManager: FactoryRoomManager;
    private backgroundMap: BackgroundMap;
    private raycaster: THREE.Raycaster;
    private mouse: THREE.Vector2;
    private hoveredObject: THREE.Object3D | null = null;
    private labelRenderer: CSS2DRenderer;
    private selectedMachine: string | null = null;
    private onMachineSelectedCallback?: (machineId?: string, elementGuid?: string) => void;
    private machineGuidMap: Map<string, string> = new Map();
    private objectToMachineMap: Map<THREE.Object3D, any> = new Map();
    private cameraAnimationId: number | null = null;
    private renderLoopId: number | null = null;
    private disposed = false;
    // Bound once so the same references can be removed again in dispose().
    private readonly handleMouseMove = (event: MouseEvent) => this.onMouseMove(event);
    private readonly handleMouseClick = (event: MouseEvent) => this.onMouseClick(event);
    private readonly handleWindowResize = () => this.onWindowResize();
    private lastVisibleMachines: string[] = [];
    private heatmapUpdateTimeout: number | null = null;
    private cachedPowerAttributes: Attribute[] = [];

    private outerwallsObj = '/mesh/outerwallsMesh251002.obj';
    private wcNamesObj = '/mesh/wc-names.obj';
    private pathWayObj = '/mesh/pathway-sefag2.obj';
    private wc274_277Obj = '/mesh/274-277.obj';

    private powerCategory = "None";
    private heatmapUpdateDelegate: ((x: number) => void) | null = null;
    private isHeatmapVisible: boolean = true;
    private lockHeatmapScale: boolean = false;
    private maxHeatValue: number = 0;

    public lockHeatmapScaling(lock: boolean) {
        this.lockHeatmapScale = lock;
    }

    public setHeatmapUpdateDelegate(delegate: (x: number) => void) {
        this.heatmapUpdateDelegate = delegate;
    }

    constructor() {
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer();

        this.renderer.setSize(window.innerWidth, window.innerHeight);
        document.body.appendChild(this.renderer.domElement);

        this.labelRenderer = this.initLabelRenderer();

        const aspect = window.innerWidth / window.innerHeight;
        this.camera = new THREE.OrthographicCamera(-100 * aspect, 100 * aspect, 100, -100, 0.1, 1000);
        this.controls = this.initControls();
        this.camera.position.set(-100, 100, 100);

        this.camera.updateProjectionMatrix();

        this.backgroundMap = new BackgroundMap();
        this.scene.add(this.backgroundMap.getObject3D());
        this.factoryMachineManager = new FactoryMachineManager();
        const ambientLight = new THREE.AmbientLight(0xffffff, 1);
        const light = new THREE.DirectionalLight(0xffffff, 0.7);
        light.position.set(50, 100, 50);
        this.scene.add(light);
        this.scene.add(ambientLight);
        this.factoryFloor = new FactoryFloorHeatMap();
        this.fetchLocations();
        this.factoryRoomManager = new FactoryRoomManager();
        loadObj(this.outerwallsObj, (obj) => {
            obj.scale.y = -1;
            obj.position.y = 1;
            const mesh = obj.children[0] as THREE.Mesh;
            mesh.material = new THREE.MeshStandardMaterial({ color: 0xffffff });

            mesh.castShadow = true;
            mesh.receiveShadow = true;
            this.scene.add(obj);
        });
        loadObj(this.wcNamesObj, (obj) => {
            obj.scale.y = 1;
            obj.position.y = 1;
            this.scene.add(obj);
        });
        loadObj(this.pathWayObj, (obj) => {
            obj.scale.y = 1;
            obj.position.y = 0.1;
            const mesh = obj.children[0] as THREE.Mesh;
            mesh.material = new THREE.MeshBasicMaterial({ color: 0x2c3c2c });
            this.scene.add(obj);
        });
        loadObj(this.wc274_277Obj, (obj) => {
            obj.scale.y = 0.8;
            obj.position.y = 1;
            this.scene.add(obj);
        });
        this.initHeatMap();
        this.initRooms();

        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();

        this.renderer.domElement.addEventListener('mousemove', this.handleMouseMove, false);
        this.renderer.domElement.addEventListener('click', this.handleMouseClick, false);


        window.addEventListener('resize', this.handleWindowResize, false);
        this.onWindowResize();
    }

    private onWindowResize() {
        // The scene fills the viewport below the content above it, e.g. the app tab bar when running in a browser.
        const top = this.renderer.domElement.getBoundingClientRect().top + window.scrollY;
        const width = window.innerWidth;
        const height = Math.max(window.innerHeight - top, 1);

        const aspect = width / height;
        this.camera.left = -100 * aspect;
        this.camera.right = 100 * aspect;
        this.camera.top = 100;
        this.camera.bottom = -100;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
        this.labelRenderer.setSize(width, height);
        this.labelRenderer.domElement.style.top = `${top}px`;
    }

    private onMouseMove(event: MouseEvent) {
        const rect = this.renderer.domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        this.checkIntersections();
    }

    private onMouseClick(event: MouseEvent) {

        const rect = this.renderer.domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        this.handleMachineClick();
    } private handleMachineClick() {
        this.raycaster.setFromCamera(this.mouse, this.camera);

        const meshObjects = Array.from(this.objectToMachineMap.keys());

        const allSceneObjects: THREE.Object3D[] = [];
        this.scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                allSceneObjects.push(child);
            }
        });

        const allIntersects = this.raycaster.intersectObjects(allSceneObjects, false);

        const intersects = this.raycaster.intersectObjects(meshObjects, false);

        if (intersects.length > 0) {
            const clickedObject = intersects[0].object;

            const machine = this.objectToMachineMap.get(clickedObject);

            if (machine) {
                const machineId = machine.getMachineId();
                this.selectMachine(machineId);
            }
        }
        else {
            this.selectMachine(null);
        }
    }

    private checkIntersections() {
        this.raycaster.setFromCamera(this.mouse, this.camera);

        const meshObjects = Array.from(this.objectToMachineMap.keys());
        const intersects = this.raycaster.intersectObjects(meshObjects, false);

        if (intersects.length > 0) {
            if (this.hoveredObject !== intersects[0].object as THREE.Object3D) {
                if (this.hoveredObject) {
                    this.hoveredObject?.userData["onHover"]?.onHoverOut();
                }
                this.hoveredObject = intersects[0].object;
                this.hoveredObject?.userData["onHover"]?.onHover();
            }
        } else {
            if (this.hoveredObject) {
                this.hoveredObject?.userData["onHover"]?.onHoverOut();
                this.hoveredObject = null;
            }
        }
    }

    public clearMachineSelection(): void {
        this.factoryMachineManager.selectMachine(null);
        this.selectedMachine = null;
    }


    public setOnMachineSelectedCallback(callback: (machineId?: string, elementGuid?: string) => void) {
        this.onMachineSelectedCallback = callback;
    }

    public async selectMachine(machineId: string | null) {

        if (machineId) {
            const success = await this.factoryMachineManager.selectMachine(machineId);

            if (!success) {
                console.warn(`FactoryScene: Failed to select machine ${machineId}`);
                return;
            }

            this.selectedMachine = machineId;

            this.animateCameraToMachine(machineId);

            const elementGuid = this.machineGuidMap.get(machineId);

            if (this.onMachineSelectedCallback) {
                this.onMachineSelectedCallback(machineId, elementGuid);
            }
        }
    }

    initLabelRenderer() {
        const labelRenderer = new CSS2DRenderer();
        labelRenderer.setSize(window.innerWidth, window.innerHeight);
        labelRenderer.domElement.style.position = 'absolute';
        labelRenderer.domElement.style.top = '0px';
        labelRenderer.domElement.style.pointerEvents = 'none';
        document.body.appendChild(labelRenderer.domElement);
        return labelRenderer;
    }

    initControls() {
        const controls = new OrbitControls(this.camera, this.renderer.domElement);
        controls.enableRotate = false;
        controls.target.set(0, 0, 0);
        controls.zoomSpeed = 1.5;
        controls.panSpeed = 1;
        controls.zoomToCursor = true;
        return controls;
    }

    async initRooms() {
        const rooms = await this.factoryRoomManager.FetchAndCreateRooms();
        for (const room of rooms) {
            this.scene.add(room.getObject3D());
        }
    }

    async initHeatMap() {
        while (this.factoryFloor.getObject3D() === undefined) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        const factoryFloorObject = this.factoryFloor.getObject3D();
        if (factoryFloorObject) {
            this.scene.add(factoryFloorObject);
        } else {
            throw new Error('Failed to add factory floor');
        }
        await this.ShowHeatMap(this.powerCategory);
    }

    public getRendererDomElement() {
        return this.renderer.domElement;
    }

    private async fetchLocations() {
        const locations = await fetchMachineLocations();

        const machinesData = this.factoryMachineManager.createMachines(locations);

        this.machineGuidMap.clear();
        machinesData.forEach(machineData => {
            if (machineData.pdp1Guid) {
                this.machineGuidMap.set(machineData.machineId, machineData.pdp1Guid);
            }
        });


        this.factoryMachineManager.getMachines().forEach((machine, index) => {
            machine.setOnClickCallback((machineId: string) => {
                this.selectMachine(machineId);
            });

            machine.getObject3D().traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    this.objectToMachineMap.set(child, machine);
                }
            });

            this.scene.add(machine.getObject3D());
        });

    }

    public async refreshHeatMap() {
        await this.ShowHeatMap(this.powerCategory);
    }

    public async refreshHeatmapWithoutFetch() {
        this.updateHeatmapVisualization(this.cachedPowerAttributes);
    }

    public getCurrentPowerCategory(): string {
        return this.powerCategory;
    }

    public toggleHeatmapVisibility(): void {
        this.isHeatmapVisible = !this.isHeatmapVisible;
        this.refreshHeatMap();
    }

    private clearMachineVisuals(): void {
        this.factoryMachineManager.getMachines().forEach(machine => {
            machine.resetColor();
            machine.resetLabelToId();
        });
    }

    private clearHeatmapVisualization(): void {
        this.clearMachineVisuals();
        if (this.factoryFloor) {
            this.factoryFloor.clearHeatPoints();
            this.factoryFloor.setBasicMaterial();
            const floorObject = this.factoryFloor.getObject3D();
            if (floorObject) {
                floorObject.visible = true;
            }
        }
        if (this.backgroundMap) {
            this.backgroundMap.setTexture('/images/FactoryMapLight.jpg');
        }
    }

    public async ShowHeatMap(category: string) {
        this.powerCategory = category;

        if (!this.isHeatmapVisible) {
            this.clearHeatmapVisualization();
            this.heatmapUpdateDelegate?.(0);
            return;
        }

        if (this.backgroundMap) {
            this.backgroundMap.setTexture('/images/FactoryMapDark.jpg');
        }

        if (this.factoryFloor) {
            this.factoryFloor.restoreHeatmapMaterial();
            const floorObject = this.factoryFloor.getObject3D();
            if (floorObject) {
                floorObject.visible = true;
            }
        }

        const powerAttributes = await this.fetchPowerAttributes(this.powerCategory);

        this.cachedPowerAttributes = powerAttributes;

        this.updateHeatmapVisualization(powerAttributes);
    }

    private updateHeatmapVisualization(powerAttributes: Attribute[]): void {

        this.clearMachineVisuals();

        if (powerAttributes.length === 0) {
            this.heatmapUpdateDelegate?.(0);
            this.factoryMachineManager.getMachines().forEach(machine => {
                machine.resetLabelToId();
            });
            if (this.factoryFloor) {
                this.factoryFloor.clearHeatPoints();
            }
            return;
        }

        const visibleMachineIds = this.getVisibleMachines();

        const visiblePowerValues = powerAttributes
            .filter(attr => visibleMachineIds.includes(attr.Element))
            .map(attr => this.factoryMachineManager.getMachine(attr.Element) ? attr.Values[0].Value as number : 0)
            .filter(value => value > 0);


        let maxPower: number;
        if (this.lockHeatmapScale) {
            maxPower = this.maxHeatValue;
        } else {
            maxPower = visiblePowerValues.length > 0
                ? Math.max(...visiblePowerValues)
                : Math.max(...powerAttributes.map(attr =>
                    this.factoryMachineManager.getMachine(attr.Element) ? attr.Values[0].Value as number : 0
                ));
        }


        const colors = ColorsArray.map(c => new THREE.Color(c)).reverse();
        const newHeatPoints: { x: number; y: number; intensity: number; radius: number; originalX?: number; originalY?: number }[] = [];


        for (const attr of powerAttributes) {
            const machine = this.factoryMachineManager.getMachine(attr.Element);

            if (machine === undefined) {
                continue;
            }

            let normalizedHeat = attr.Values[0].Value as number / maxPower;
            const position = machine.getPosition();
            if (isNaN(normalizedHeat)) {
                machine.resetLabelToId();
                continue;
            }

            normalizedHeat = clamp(normalizedHeat, 0, 1);

            const actualPowerValue = attr.Values[0].Value as number;

            const numColors = ColorsArray.length;
            const colorIndex = clamp(Math.floor(normalizedHeat * numColors), 0, numColors - 1);
            const heatColor = new THREE.Color();
            colors[0] = new THREE.Color(0x181818);
            if (colorIndex < numColors - 1) {
                heatColor.lerpColors(colors[colorIndex], colors[colorIndex + 1], normalizedHeat * numColors - colorIndex);
                machine.setColor(heatColor.getHex());
            } else {
                machine.setColor(colors[colorIndex].getHex());
            }

            if (actualPowerValue > 0.001) {
                newHeatPoints.push({
                    x: position.x,
                    y: position.z,
                    intensity: normalizedHeat,
                    radius: 0.6,
                    originalX: position.x,
                    originalY: position.z
                });
            }

            const kw = actualPowerValue;
            const labelHtml = `<div style="text-align: center;">${attr.Element}<br>${kw < 1 ? kw.toFixed(4) : kw.toFixed(1)} kW</div>`;
            machine.setInnerHtml(labelHtml);
        }

        if (this.factoryFloor) {
            this.factoryFloor.updateHeatPointsWithTransition(newHeatPoints);
        }

        this.maxHeatValue = maxPower;
        this.heatmapUpdateDelegate?.(maxPower);
    }

    private async fetchPowerAttributes(key: string) {
        let energyConsumption: Attribute[] | undefined;

        switch (key) {
            case "Inserts":
                energyConsumption = await fetchNormalizedByInserts("Power consumption");
                break;
            case "Order":
                energyConsumption = await fetchNormalizedByOrders("Power consumption");
                break;
            case "None":
                energyConsumption = await fetchTotalPowerConsumption();
                break;
            default:
                console.error(`Unknown key: ${key}`);
                break;
        }

        if (energyConsumption === undefined) {
            console.error("Failed to fetch power consumption");
            return [];
        }

        const filteredEnergyConsumption = energyConsumption.filter(attr => isNaN(attr.Values[0].Value as number) === false);
        return filteredEnergyConsumption;
    }


    public getAvailableMachines(): string[] {
        return this.factoryMachineManager.getAvailableMachines();
    }

    public getSearchSuggestions(searchTerm: string, maxResults: number = 4): string[] {
        return this.factoryMachineManager.getSmartSearchSuggestions(searchTerm, maxResults);
    }

    public getMachineCount(): number {
        return this.factoryMachineManager.getMachineCount();
    }


    public centerCameraOnMachine(machineId: string): boolean {
        const machine = this.factoryMachineManager.getMachine(machineId);
        if (!machine) {
            console.warn(`FactoryScene: Cannot center camera on machine ${machineId} - not found`);
            return false;
        }
        this.animateCameraToMachine(machineId);
        return true;
    }

    public searchAndSelectMachine(machineId: string): boolean {
        const success = this.factoryMachineManager.searchAndSelectMachine(machineId);
        if (!success) {
            console.warn(`FactoryScene: Machine ${machineId} not found`);
            return false;
        }

        this.selectedMachine = machineId;

        this.animateCameraToMachine(machineId);

        const elementGuid = this.machineGuidMap.get(machineId);
        if (this.onMachineSelectedCallback) {
            this.onMachineSelectedCallback(machineId, elementGuid);
        }

        return true;
    }

    public animate() {
        if (this.disposed) {
            return;
        }
        this.renderLoopId = requestAnimationFrame(() => this.animate());
        this.factoryFloor.setRadius(1 - (this.camera.zoom * 0.03 + 0.5));
        this.controls.update();

        this.logVisibleMachines();

        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.render(this.scene, this.camera);
    }

    private logVisibleMachines(): void {
        const visibleMachines = this.getVisibleMachines();

        const hasChanged =
            visibleMachines.length !== this.lastVisibleMachines.length ||
            !visibleMachines.every(id => this.lastVisibleMachines.includes(id)) ||
            !this.lastVisibleMachines.every(id => visibleMachines.includes(id));

        if (hasChanged) {
            this.lastVisibleMachines = visibleMachines;

            if (this.isHeatmapVisible && this.cachedPowerAttributes.length > 0) {

                if (this.heatmapUpdateTimeout !== null) {
                    clearTimeout(this.heatmapUpdateTimeout);
                }

                this.heatmapUpdateTimeout = window.setTimeout(() => {

                    this.updateHeatmapVisualization(this.cachedPowerAttributes);
                    this.heatmapUpdateTimeout = null;
                }, 300);
            }
        }
    }

    public getVisibleMachines(): string[] {
        this.camera.updateMatrixWorld();

        const frustum = new THREE.Frustum();
        const projectionMatrix = new THREE.Matrix4().multiplyMatrices(
            this.camera.projectionMatrix,
            this.camera.matrixWorldInverse
        );
        frustum.setFromProjectionMatrix(projectionMatrix);

        const visibleMachines: string[] = [];
        const machines = this.factoryMachineManager.getMachines();

        machines.forEach(machine => {
            const position = machine.getPosition();

            if (frustum.containsPoint(position)) {
                visibleMachines.push(machine.getMachineId());
            }
        });

        return visibleMachines;
    }

    private animateCameraToMachine(machineId: string): void {
        const machine = this.factoryMachineManager.getMachine(machineId);
        if (!machine) {
            console.warn(`FactoryScene: Cannot animate to machine ${machineId} - not found`);
            return;
        }

        const machinePosition = machine.getPosition();
        const targetPosition = new THREE.Vector3(machinePosition.x, machinePosition.y, machinePosition.z);

        if (this.cameraAnimationId) {
            cancelAnimationFrame(this.cameraAnimationId);
        }

        const startCameraPosition = this.camera.position.clone();
        const startControlsTarget = this.controls.target.clone();

        const currentOffset = startCameraPosition.clone().sub(startControlsTarget);
        const targetCameraPosition = targetPosition.clone().add(currentOffset);

        const animationDuration = 1200;
        const startTime = Date.now();

        const animateFrame = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            const easeProgress = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            this.camera.position.lerpVectors(startCameraPosition, targetCameraPosition, easeProgress);

            this.controls.target.lerpVectors(startControlsTarget, targetPosition, easeProgress);

            this.controls.update();

            if (progress < 1) {
                this.cameraAnimationId = requestAnimationFrame(animateFrame);
            } else {
                this.cameraAnimationId = null;
            }
        };

        animateFrame();
    }

    public dispose(): void {
        this.disposed = true;
        this.renderer.domElement.removeEventListener('mousemove', this.handleMouseMove);
        this.renderer.domElement.removeEventListener('click', this.handleMouseClick);
        window.removeEventListener('resize', this.handleWindowResize);

        if (this.renderLoopId !== null) {
            cancelAnimationFrame(this.renderLoopId);
            this.renderLoopId = null;
        }

        if (this.cameraAnimationId) {
            cancelAnimationFrame(this.cameraAnimationId);
            this.cameraAnimationId = null;
        }

        if (this.heatmapUpdateTimeout !== null) {
            clearTimeout(this.heatmapUpdateTimeout);
            this.heatmapUpdateTimeout = null;
        }

        this.controls.dispose();
        this.renderer.dispose();
        // The canvas and label layer are attached to <body>, so they outlive the view unless removed here.
        this.renderer.domElement.remove();
        if (this.labelRenderer.domElement.parentNode) {
            this.labelRenderer.domElement.parentNode.removeChild(this.labelRenderer.domElement);
        }
    }
}

export default FactoryScene;